//frontend/src/pages/client/HomePage.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaShippingFast, FaBoxOpen, FaMapMarkerAlt,
  FaCheckCircle, FaClock, FaDollarSign, FaGlobe,
  FaTimes, FaPhone, FaUser, FaBox, FaWeight, FaCube,
  FaImage, FaFileAlt, FaPaperPlane, FaStar, FaTruck
} from 'react-icons/fa';
import * as FaIcons from 'react-icons/fa';
import HeroSection from '../../components/client/HeroSection';
import axiosInstance from '../../axios';
import '../../styles/client.css';

const HomePage = () => {
  const navigate = useNavigate();
  const ctaRef = useRef(null);
  
  const [features, setFeatures] = useState([]);
  const [services, setServices] = useState([]);
  const [contactInfo, setContactInfo] = useState([]);
  const [ctaSettings, setCtaSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // SLIDER STATE
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const [showContactModal, setShowContactModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [formData, setFormData] = useState({
    sender_name: '',
    sender_phone: '',
    sender_phone_alt: '',
    cargo_name: '',
    measurement_type: 'weight',
    weight_kg: '',
    length_cm: '',
    width_cm: '',
    height_cm: '',
    notes: ''
  });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calculatedVolume, setCalculatedVolume] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        const [featuresRes, servicesRes, contactRes, ctaRes] = await Promise.all([
          axiosInstance.get('/api/client/features'),
          axiosInstance.get('/api/client/services'),
          axiosInstance.get('/api/client/contact-info'),
          axiosInstance.get('/api/client/cta-settings')
        ]);
        
        if (featuresRes.data.success) setFeatures(featuresRes.data.data);
        if (servicesRes.data.success) setServices(servicesRes.data.data);
        if (contactRes.data.success) setContactInfo(contactRes.data.data);
        if (ctaRes.data.success) setCtaSettings(ctaRes.data.data);
        
      } catch (error) {
        console.error('Мэдээлэл татахад алдаа:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // AUTO SLIDE
  useEffect(() => {
    if (services.length > 3) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % services.length);
      }, 15000);
      
      return () => clearInterval(interval);
    }
  }, [services.length]);

  const getIconComponent = (iconName) => {
    const IconComponent = FaIcons[iconName] || FaCheckCircle;
    return <IconComponent />;
  };

  const getIconColorClass = (color) => {
    const colorMap = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500',
      yellow: 'bg-yellow-500',
      pink: 'bg-pink-500',
      indigo: 'bg-indigo-500'
    };
    return colorMap[color] || 'bg-blue-500';
  };

  const getTextColorClass = (color) => {
    const colorMap = {
      blue: 'text-blue-600',
      green: 'text-green-600',
      purple: 'text-purple-600',
      orange: 'text-orange-600',
      red: 'text-red-600',
      yellow: 'text-yellow-600',
      pink: 'text-pink-600',
      indigo: 'text-indigo-600'
    };
    return colorMap[color] || 'text-blue-600';
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const getVisibleServices = () => {
    if (services.length <= 3) return services;
    
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentSlide + i) % services.length;
      visible.push(services[index]);
    }
    return visible;
  };

  const calculateVolume = (length, width, height) => {
    if (length && width && height) {
      const volume = (length * width * height) / 1000000;
      setCalculatedVolume(volume.toFixed(4));
    } else {
      setCalculatedVolume(0);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (['length_cm', 'width_cm', 'height_cm'].includes(name)) {
      const newData = { ...formData, [name]: value };
      calculateVolume(newData.length_cm, newData.width_cm, newData.height_cm);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);
        
        const res = await axiosInstance.post('/api/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        return res.data.imageUrl;
      });

      const urls = await Promise.all(uploadPromises);
      setUploadedImages(prev => [...prev, ...urls]);
    } catch (error) {
      alert('Зураг оруулахад алдаа гарлаа: ' + error.message);
    }
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sender_name || !formData.sender_phone) {
      alert('Нэр болон утасны дугаараа оруулна уу');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        ...formData,
        volume_cbm: formData.measurement_type === 'volume' ? calculatedVolume : null,
        cargo_images: uploadedImages
      };

      const res = await axiosInstance.post('/api/client/cargo-requests', submitData);
      
      if (res.data.success) {
        alert(`✅ Дуудлага амжилттай илгээгдлээ!\n\nДуудлагын код: ${res.data.request_code}\n\nБид тантай удахгүй холбогдох болно.`);
        
        setFormData({
          sender_name: '',
          sender_phone: '',
          sender_phone_alt: '',
          cargo_name: '',
          measurement_type: 'weight',
          weight_kg: '',
          length_cm: '',
          width_cm: '',
          height_cm: '',
          notes: ''
        });
        setUploadedImages([]);
        setCalculatedVolume(0);
        setShowRequestModal(false);
      }
    } catch (error) {
      alert('Алдаа гарлаа: ' + (error.response?.data?.msg || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactPhones = ctaSettings?.phone_numbers || [
    { label: 'Солонгос 1', number: '070-8018-8678', flag: '🇰🇷' },
    { label: 'Солонгос 2', number: '010-5815-3005', flag: '🇰🇷' },
    { label: 'Монгол 1', number: '+976-7786-8678', flag: '🇲🇳' }
  ];

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Ачааллаж байна...</p>
      </div>
    );
  }

  return (
    <div className="client-homepage">
      <HeroSection />

      {/* FEATURES SECTION */}
      <section className="features-section">
        <div className="container-custom">
          <h2 className="section-title">Манай давуу тал</h2>
          <div className="features-grid">
            {features.map((feature) => (
              <div key={feature.id} className="feature-card">
                <div className={`feature-icon ${getIconColorClass(feature.icon_color)}`}>
                  {getIconComponent(feature.icon_name)}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES SECTION WITH SLIDER */}
      <section className="services-section" id="services">
        <div className="container-custom">
          <h2 className="section-title">Ачаа тээврийн төрөл</h2>
          
          {services.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Үйлчилгээ байхгүй байна
            </div>
          ) : services.length <= 3 ? (
            <div className="services-grid">
              {services.map((service) => (
                <div 
                  key={service.id} 
                  className={`service-card ${service.is_featured ? 'service-card-featured' : ''}`}
                >
                  {service.badge_text && (
                    <div className="service-badge">{service.badge_text}</div>
                  )}
                  <div className="service-icon-wrapper">
                    <span className={`service-icon ${getTextColorClass(service.icon_color)}`}>
                      {getIconComponent(service.icon_name)}
                    </span>
                  </div>
                  <h3 className="service-title">{service.service_name}</h3>
                  <ul className="service-list">
                    {service.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <div className="services-slider-container">
              <div className="services-grid">
                {getVisibleServices().map((service, idx) => (
                  <div 
                    key={`${service.id}-${idx}`}
                    className={`service-card ${service.is_featured ? 'service-card-featured' : ''}`}
                  >
                    {service.badge_text && (
                      <div className="service-badge">{service.badge_text}</div>
                    )}
                    <div className="service-icon-wrapper">
                      <span className={`service-icon ${getTextColorClass(service.icon_color)}`}>
                        {getIconComponent(service.icon_name)}
                      </span>
                    </div>
                    <h3 className="service-title">{service.service_name}</h3>
                    <ul className="service-list">
                      {service.features.map((feature, featureIdx) => (
                        <li key={featureIdx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="slider-dots-only">
                {services.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`slider-dot ${currentSlide === index ? 'active' : ''}`}
                    aria-label={`Slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="cta-section" ref={ctaRef} id="cta">
        <div className="container-custom">
          <div className="cta-content">
            <h2 className="cta-title">
              {ctaSettings?.section_title || 'Ачаа илгээх гэж байна уу?'}
            </h2>
            <p className="cta-description">
              {ctaSettings?.section_description || 'Бидэнтэй холбогдоорой, таны ачааг найдвартай хүргүүлээрэй'}
            </p>
            <div className="cta-buttons">
              <button 
                onClick={() => setShowContactModal(true)}
                className="cta-btn cta-btn-primary"
              >
                <FaPhone className="mr-2" />
                Холбоо барих
              </button>
              
              <button 
                onClick={() => setShowRequestModal(true)}
                className="cta-btn cta-btn-secondary"
              >
                <FaBoxOpen className="mr-2" />
                Дуудлага өгөх
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT INFO */}
      <section className="contact-info-section" id="contact">
        <div className="container-custom">
          <div className="contact-info-grid">
            {contactInfo.map((section) => (
              <div key={section.id} className="contact-info-card">
                <h4 className="contact-info-title">{section.section_title}</h4>
                <div className="contact-info-list">
                  {section.contact_items.map((item, idx) => (
                    <p key={idx}>
                      {item.includes('@') ? '📧' : 
                       item.includes('KakaoTalk') ? '💬' : 
                       item.includes('Facebook') ? '📘' : '📞'} {item}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ХОЛБОО БАРИХ MODAL */}
      {showContactModal && (
        <div className="contact-modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close-btn"
              onClick={() => setShowContactModal(false)}
            >
              <FaTimes />
            </button>

            <div className="contact-modal-header">
              <div className="contact-modal-icon">
                <FaPhone />
              </div>
              <h2 className="contact-modal-title">Холбоо барих</h2>
              <p className="contact-modal-subtitle">Жолоочтой шууд холбогдох</p>
            </div>

            <div className="contact-phones-list">
              {Array.isArray(contactPhones) ? contactPhones.map((phone, index) => (
                <a 
                  key={index}
                  href={`tel:${typeof phone === 'string' ? phone : phone.number?.replace(/[^0-9+]/g, '')}`}
                  className="contact-phone-item"
                >
                  <span className="contact-phone-flag">{phone.flag || '📞'}</span>
                  <div className="contact-phone-details">
                    <span className="contact-phone-label">{phone.label || 'Утас'}</span>
                    <span className="contact-phone-number">{typeof phone === 'string' ? phone : phone.number}</span>
                  </div>
                  <FaPhone className="contact-phone-icon" />
                </a>
              )) : <p>Холбоо барих мэдээлэл байхгүй</p>}
            </div>
          </div>
        </div>
      )}

      {/* АЧАА ДУУДЛАГА MODAL */}
      {showRequestModal && (
        <div className="cargo-request-modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="cargo-request-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close-btn"
              onClick={() => setShowRequestModal(false)}
            >
              <FaTimes />
            </button>

            <div className="modal-header-enhanced ">
              <div className="modal-icon">
                <FaBoxOpen />
              </div>
              <h2 className="modal-title">Ачаа дуудлага өгөх</h2>
              <p className="modal-subtitle">Бид тантай удахгүй холбогдох болно</p>
            </div>

            <form onSubmit={handleSubmit} className="cargo-request-form">
              {/* Илгээгчийн мэдээлэл */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <FaUser /> Илгээгчийн мэдээлэл
                </h3>
                
                <div className="form-group">
                  <label>Нэр <span className="required">*</span></label>
                  <input
                    type="text"
                    name="sender_name"
                    value={formData.sender_name}
                    onChange={handleInputChange}
                    placeholder="Таны нэр"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Утасны дугаар <span className="required">*</span></label>
                    <input
                      type="tel"
                      name="sender_phone"
                      value={formData.sender_phone}
                      onChange={handleInputChange}
                      placeholder="99001122"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Нэмэлт утас</label>
                    <input
                      type="tel"
                      name="sender_phone_alt"
                      value={formData.sender_phone_alt}
                      onChange={handleInputChange}
                      placeholder="88001122"
                    />
                  </div>
                </div>
              </div>

              {/* Ачааны мэдээлэл */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <FaBox /> Ачааны мэдээлэл
                </h3>
                
                <div className="form-group">
                  <label>Ачааны нэр</label>
                  <input
                    type="text"
                    name="cargo_name"
                    value={formData.cargo_name}
                    onChange={handleInputChange}
                    placeholder="Жишээ: Хувцас, Электрон бараа"
                  />
                </div>

                <div className="form-group">
                  <label>Хэмжих төрөл <span className="required">*</span></label>
                  <select
                    name="measurement_type"
                    value={formData.measurement_type}
                    onChange={handleInputChange}
                  >
                    <option value="weight">Жингээр тооцоолох</option>
                    <option value="volume">Эзэлхүүнээр тооцоолох</option>
                  </select>
                </div>

                {formData.measurement_type === 'weight' && (
                  <div className="form-group">
                    <label>
                      <FaWeight /> Жин (кг)
                    </label>
                    <input
                      type="number"
                      name="weight_kg"
                      value={formData.weight_kg}
                      onChange={handleInputChange}
                      placeholder="50"
                      step="0.01"
                    />
                  </div>
                )}

                {formData.measurement_type === 'volume' && (
                  <>
                    <div className="form-row-three">
                      <div className="form-group">
                        <label>Урт (см)</label>
                        <input
                          type="number"
                          name="length_cm"
                          value={formData.length_cm}
                          onChange={handleInputChange}
                          placeholder="100"
                          step="0.1"
                        />
                      </div>
                      <div className="form-group">
                        <label>Өргөн (см)</label>
                        <input
                          type="number"
                          name="width_cm"
                          value={formData.width_cm}
                          onChange={handleInputChange}
                          placeholder="50"
                          step="0.1"
                        />
                      </div>
                      <div className="form-group">
                        <label>Өндөр (см)</label>
                        <input
                          type="number"
                          name="height_cm"
                          value={formData.height_cm}
                          onChange={handleInputChange}
                          placeholder="30"
                          step="0.1"
                        />
                      </div>
                    </div>
                    
                    {calculatedVolume > 0 && (
                      <div className="volume-display">
                        <FaCube />
                        <span>Эзэлхүүн: <strong>{calculatedVolume} м³</strong></span>
                      </div>
                    )}
                  </>
                )}

                <div className="form-group">
                  <label>
                    <FaImage /> Ачааны зураг
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="file-input"
                  />
                  
                  {uploadedImages.length > 0 && (
                    <div className="image-preview-grid">
                      {uploadedImages.map((url, index) => (
                        <div key={index} className="image-preview-item">
                          <img src={`http://localhost:5000${url}`} alt={`Preview ${index + 1}`} />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="image-remove-btn"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    <FaFileAlt /> Нэмэлт тайлбар
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Ачааны талаар нэмэлт мэдээлэл..."
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowRequestModal(false)}
                  className="btn-cancel"
                >
                  Хаах
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-submit"
                >
                  {isSubmitting ? (
                    <>
                      <div className="btn-spinner"></div>
                      Илгээж байна...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane />
                      Дуудлага өгөх
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;